'use client';

import { useEffect, useRef, useState } from 'react';
import { useHierarchicalMenu } from 'react-instantsearch';
import type { HierarchicalMenuItem } from 'instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu';

const LIEU_ATTRIBUTES = ['lieu.lvl0', 'lieu.lvl1', 'lieu.lvl2'];

interface RefineFn {
  (value: string): void;
}

/**
 * ffbbsite_jobs - Hierarchical location menu component
 */
export default function LieuHierarchicalMenu() {
  const { items, refine, canRefine } = useHierarchicalMenu({
    attributes: LIEU_ATTRIBUTES,
    limit: 1000,
    sortBy: ['name:asc'],
  });

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!canRefine && items.length === 0) return null;

  const selectedLabel = findSelectedLabel(items);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="select select-bordered flex max-w-xs items-center justify-between gap-2 rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
      >
        <span className="truncate">{selectedLabel || 'Lieu'}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 z-30 mt-2 max-h-80 w-72 overflow-y-auto rounded-2xl border border-zinc-200 bg-white p-3 shadow-lg">
          <LieuLevel items={items} refine={refine} depth={0} />
        </div>
      )}
    </div>
  );
}

function LieuLevel({
  items,
  refine,
  depth,
}: {
  items: HierarchicalMenuItem[];
  refine: RefineFn;
  depth: number;
}) {
  if (!items || items.length === 0) return null;

  return (
    <ul className={depth > 0 ? 'ml-4 border-l border-zinc-100 pl-2' : ''}>
      {items.map((item) => (
        <li key={item.value}>
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-zinc-50">
            <input
              type="checkbox"
              className="checkbox checkbox-sm shrink-0"
              checked={item.isRefined}
              onChange={() => refine(item.value)}
            />
            <span className={item.isRefined ? 'font-bold' : ''}>{item.label}</span>
            <span className="ml-auto pl-2 text-xs text-zinc-400">{item.count}</span>
          </label>
          {item.isRefined && item.data && item.data.length > 0 && (
            <LieuLevel items={item.data} refine={refine} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  );
}

function findSelectedLabel(items: HierarchicalMenuItem[]): string | null {
  for (const item of items) {
    if (item.isRefined) {
      const childLabel = item.data ? findSelectedLabel(item.data) : null;
      return childLabel || item.label;
    }
  }
  return null;
}
